import React, { useEffect, useState } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";

import cloudinaryEvents from "../data/cloudinaryEvents.json";

const existingEventLinks = [
  { to: "/events/birthday", label: "Birthday Parties" },
  { to: "/events/womensday", label: "Womensday Events" },
  { to: "/events/cradle", label: "Cradle Ceremony" },
];

const newEventLinks = cloudinaryEvents.map(({ slug, label, folder }) => ({
  to: `/events/${slug}`,
  label,
  folder,
}));

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/weddings", label: "Weddings" },
  { to: "/rentals", label: "Decor Rentals" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  const location = useLocation();
  const eventsOpen = showDropdown || showMobileMenu;
  const isLocal = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

  useEffect(() => {
    if (!eventsOpen || isLocal) return;
    const controller = new AbortController();
    Promise.all(newEventLinks.map(async event => {
      try {
        const response = await fetch(`/api/synced-images?folder=${encodeURIComponent(event.folder)}&limit=1`, {
          signal: controller.signal,
        });
        const photos = response.ok ? await response.json() : [];
        return Array.isArray(photos) && photos.length ? event : null;
      } catch {
        return null;
      }
    })).then(links => {
      if (!controller.signal.aborted) setAvailableEvents(links.filter(Boolean));
    });
    return () => controller.abort();
  }, [eventsOpen, isLocal]);

  const eventLinks = [...existingEventLinks, ...(isLocal ? newEventLinks : availableEvents)];

  const isActive = (path) => location.pathname === path;

  const linkClass = (path) =>
    `relative elegant-font font-semibold text-gray-900 transition-all duration-300 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:bg-yellow-400 after:transition-all after:duration-300 ${
      isActive(path) ? "after:w-0" : "after:w-0 hover:after:w-full"
    }`;

  const closeMenus = () => {
    setShowDropdown(false);
    setShowMobileMenu(false);
  };

  const eventsButtonClass = "text-gray-900 elegant-font font-semibold hover:text-yellow-600";

  return (
    <header className="sticky top-0 bg-pink-50 z-50 shadow-md">
      <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-3 px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img src={logo} alt="Fiesta Logo" className="h-10 sm:h-12 mr-1 flex-shrink-0" />
          <Link
            to="/"
            className="text-2xl min-[380px]:text-3xl text-gradient truncate bg-gradient-to-r from-yellow-600 to-yellow-400 bg-clip-text text-transparent elegant-font leading-tight"
            onClick={closeMenus}
          >
            Fiesta Event Decor
          </Link>
        </div>

        <button
          type="button"
          className="md:hidden text-gray-800 text-2xl p-3 -mr-2"
          onClick={() => setShowMobileMenu((current) => !current)}
          aria-label={showMobileMenu ? "Close navigation" : "Open navigation"}
          aria-expanded={showMobileMenu}
        >
          {showMobileMenu ? <FaTimes /> : <FaBars />}
        </button>

        <nav className="hidden md:flex space-x-6 text-xl font-serif items-center">
          {navLinks.slice(0, 3).map((link) => (
            <Link key={link.to} to={link.to} className={linkClass(link.to)}>
              {link.label}
            </Link>
          ))}

          <div className="relative group">
            <button
              type="button"
              onClick={() => setShowDropdown((current) => !current)}
              className={eventsButtonClass}
              aria-expanded={showDropdown}
            >
              Events ▼
            </button>
            {showDropdown && (
              <div className="absolute top-8 left-0 bg-white border border-gray-200 rounded shadow-md py-2 z-50 w-48">
                {eventLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block px-4 py-2 hover:bg-yellow-50 text-gray-700 elegant-font"
                    onClick={closeMenus}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/contact" className={linkClass("/contact")}>Contact</Link>
        </nav>
      </div>

      {showMobileMenu && (
        <nav className="md:hidden border-t border-pink-100 bg-pink-50 px-4 pb-4 text-xl font-serif shadow-sm">
          <div className="flex flex-col gap-1 pt-2">
            {navLinks.slice(0, 3).map((link) => (
              <Link key={link.to} to={link.to} className={`${linkClass(link.to)} py-2`} onClick={closeMenus}>
                {link.label}
              </Link>
            ))}
            <div className="border-t border-pink-100 pt-3 mt-1">
              <p className={`${eventsButtonClass} py-1`}>Events</p>
              <div className="mt-1 flex flex-col gap-1 pl-4">
                {eventLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="text-gray-700 elegant-font hover:text-yellow-600 py-2"
                    onClick={closeMenus}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <Link to="/contact" className={`${linkClass("/contact")} py-2`} onClick={closeMenus}>
              Contact
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
